/*
 * Behavioural tracking — navigation, mega-menu panels, filters, sort, search, quiz.
 *
 * Answers the questions raised on 8 Sep that heatmaps cannot: how many clicks go
 * into each menu item, and whether the scent / occasion filters are actually used.
 * Search and quiz were added 10 Sep at Johanna's request — search had been dropped
 * from scope on 9 Sep only because the results page did not exist yet.
 *
 * Two destinations, for two different reasons:
 *
 *   • GA4 — via the global `gtag` the Leaf Signal app embed defines in page scope
 *     (it loads gtag.js itself and points GA4 at the first-party proxy
 *     signal.notedaromas.co.uk with first_party_collection). So we can SEND events
 *     today with no Google account access. Note that event COUNTS report straight
 *     away, but the per-item breakdown needs each custom parameter registering as a
 *     custom dimension in the GA4 admin, which does need account access. Until then
 *     GA4 answers "how many nav clicks", and Clarity answers "which items".
 *
 *   • Microsoft Clarity — `clarity('set', ...)` writes a session tag we can filter
 *     and segment on in the dashboard immediately (we own that project), and
 *     `clarity('event', ...)` marks the moment in the recording so you can jump to
 *     it. Session-scoped and dashboard-only: custom tags are NOT exposed by the
 *     Clarity export API, so clarity.py cannot pull them.
 *
 * Both are optional at runtime. If either global is missing (consent not granted,
 * app embed off, blocker) the other still fires and nothing throws.
 *
 * Listeners are delegated off `document` in the CAPTURE phase, deliberately:
 * collection-grid.js intercepts filter clicks to fetch and patch in place, and
 * capture runs before any handler can preventDefault or stop propagation. That
 * also means swapped-in markup needs no re-init.
 */
(function () {
  'use strict';

  var MAX_LABEL = 60;

  /* Readable label for an element: collapsed text, else aria-label, else title. */
  function labelOf(el) {
    if (!el) return '';
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) text = (el.getAttribute('aria-label') || '').trim();
    if (!text) text = (el.getAttribute('title') || '').trim();
    return text.slice(0, MAX_LABEL);
  }

  /* Fire to both destinations. `tag` is the Clarity session tag {key, value}. */
  function send(name, params, tag) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
    } catch (e) { /* never let tracking break the page */ }

    try {
      if (typeof window.clarity === 'function') {
        if (tag && tag.key && tag.value) window.clarity('set', tag.key, String(tag.value));
        window.clarity('event', name);
      }
    } catch (e) { /* same */ }
  }

  /* Where in the navigation a click happened. Order matters: panel before drawer. */
  function navLocation(el) {
    if (el.closest('[data-panel]')) return 'mega_panel';
    if (el.closest('[data-header-drawer]')) return 'drawer';
    if (el.closest('[data-header-nav]')) return 'header';
    if (el.closest('.header-navstrip')) return 'navstrip';
    return '';
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    /* ── Mega-menu panel opened ───────────────────────────────────────────── */
    var opener = target.closest('[data-drawer-open-panel]');
    if (opener) {
      var panel = opener.getAttribute('data-drawer-open-panel') || labelOf(opener);
      send('nav_panel_open', { nav_item: panel }, { key: 'nav_panel', value: panel });
      return;
    }

    /* ── Filter applied or removed ────────────────────────────────────────── */
    var filter = target.closest('[data-filter-link]');
    if (filter) {
      /* The bar carries the key on [data-facet-panel]; the mobile drawer's
         accordion carries it on [data-acc] instead. Chips have neither. */
      var key = '';
      var panelEl = filter.closest('[data-facet-panel]');
      if (panelEl) {
        key = panelEl.getAttribute('data-filter-key') || '';
      } else {
        var accEl = filter.closest('[data-acc]');
        if (accEl) key = accEl.getAttribute('data-acc-key') || '';
      }

      /* Take the label span, never the whole cell: the sibling count span makes
         the text inventory-dependent, so "In stock (33)" and "In stock (31)"
         would aggregate as two different values. Strip a trailing count as a
         fallback for markup that has no label span (the applied chips). */
      var labelEl = filter.querySelector('.cf-cell__label, .cf-check__label');
      var value = labelEl ? labelOf(labelEl) : labelOf(filter).replace(/\s*\(\d+\)\s*$/, '');
      /* Chips in the applied row remove; cells toggle on their pressed state. */
      var isRemove = filter.classList.contains('cf-chip') ||
        filter.getAttribute('aria-pressed') === 'true' ||
        filter.getAttribute('aria-checked') === 'true';

      send('filter_apply', {
        filter_key: key,
        filter_value: value,
        filter_action: isRemove ? 'remove' : 'add'
      }, { key: 'filter', value: (key ? key + ':' : '') + value });
      return;
    }

    /* ── Sort changed ─────────────────────────────────────────────────────── */
    var sort = target.closest('[data-sort-link]');
    if (sort) {
      var sortValue = sort.getAttribute('data-sort-value') || labelOf(sort);
      send('sort_apply', { sort_value: sortValue }, { key: 'sort', value: sortValue });
      return;
    }

    /* ── Navigation link ──────────────────────────────────────────────────── */
    var link = target.closest('a[href]');
    if (!link) return;

    var where = navLocation(link);
    if (!where) return;

    /* Utility controls that live inside the nav regions but are not navigation. */
    if (link.matches('[data-cart-drawer-trigger], [data-region-trigger], [data-header-logo], .swym-wishlist')) return;

    var item = labelOf(link);
    if (!item) return;

    send('nav_click', {
      nav_item: item,
      nav_location: where,
      nav_url: link.getAttribute('href') || ''
    }, { key: 'nav_item', value: item });
  }, true);

  /* ── Search ───────────────────────────────────────────────────────────────
     Three questions: how many people open search, what they type, and whether
     it worked. The drawer answers the first two; the results page answers the
     third, because only the server knows how many hits a term returned. */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;

    /* Search opened — the header bar is a trigger, not a real input. */
    if (t.closest('[data-search-trigger]')) {
      send('search_open', { search_location: window.innerWidth < 768 ? 'mobile' : 'desktop' },
        { key: 'search', value: 'opened' });
      return;
    }

    /* A product result clicked, in the drawer or on the results page. The two
       are separated because a click in the drawer means predictive answered the
       question, while a click on the page means it did not. */
    var drawerProduct = t.closest('[data-search-product]');
    if (drawerProduct) {
      send('search_result_click', {
        search_term: currentTerm(),
        result_url: drawerProduct.getAttribute('href') || '',
        result_source: t.closest('[data-search-presets]') ? 'bestsellers' : 'predictive'
      }, { key: 'search_click', value: 'predictive' });
      return;
    }

    var pageCard = t.closest('.search-results [data-product-card]');
    if (pageCard) {
      send('search_result_click', {
        search_term: currentTerm(),
        result_url: pageCard.getAttribute('data-product-url') || '',
        result_source: 'results_page'
      }, { key: 'search_click', value: 'results_page' });
      return;
    }

    /* The preset category chips are a different intent: the shopper abandoned
       the term and browsed instead. Counting these as results would overstate
       how well search itself is answering. */
    var chip = t.closest('[data-search-presets] a[href]');
    if (chip) {
      send('search_chip_click', {
        chip_label: labelOf(chip),
        chip_url: chip.getAttribute('href') || ''
      }, { key: 'search_chip', value: labelOf(chip) });
    }
  }, true);

  /* Term submitted from the drawer or the results page's own refine box. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-search-form], .search-head__form');
    if (!form) return;
    var input = form.querySelector('input[name="q"]');
    var term = input && input.value.trim();
    if (!term) return;
    send('search_submit', { search_term: term.slice(0, MAX_LABEL) },
      { key: 'search_term', value: term.slice(0, MAX_LABEL) });
  }, true);

  /* The term this page was rendered for, so a result click can be attributed. */
  function currentTerm() {
    try {
      var q = new URL(window.location.href).searchParams.get('q');
      if (q) return q.slice(0, MAX_LABEL);
    } catch (e) { /* fall through */ }
    var live = document.querySelector('[data-search-input]');
    return live && live.value ? live.value.trim().slice(0, MAX_LABEL) : '';
  }

  /* Results page landing: one event carrying the outcome. A zero-result search
     is the single most actionable thing here, so it gets its own event name
     rather than a parameter that has to be filtered for. */
  (function reportSearchPage() {
    var page = document.querySelector('.search-results[data-collection-grid]');
    if (!page) return;
    var term = currentTerm();
    if (!term) return;
    var region = page.querySelector('[data-load-more-region]');
    var total = region ? parseInt(region.getAttribute('data-total'), 10) : 0;
    if (!total || isNaN(total)) {
      send('search_no_results', { search_term: term },
        { key: 'search_no_results', value: term });
    } else {
      send('search_results', { search_term: term, result_count: total },
        { key: 'search_results', value: term });
    }
  }());

  /* ── Quiz ─────────────────────────────────────────────────────────────────
     Click rate, engagement and completion, off the `quiz:screen` event that
     assets/quiz.js emits on every screen change. Engagement fires once per
     session-ish (first question screen), so it can be read as a rate against
     opens rather than counting every step. */

  var quizSeen = {};
  var quizStarted = false;

  /* quiz.js already announces opening, and that covers the cross-page
     href="#anchor" triggers a [data-quiz-open] click listener would miss. */
  document.addEventListener('quiz:open', function (e) {
    quizStarted = true;
    var anchor = (e && e.detail && e.detail.anchor) || 'quiz';
    send('quiz_open', { quiz_source: anchor }, { key: 'quiz', value: 'opened' });
  });

  /* An inline quiz (one not inside a drawer) never fires quiz:open, so first
     interaction starts it instead. */
  document.addEventListener('click', function (e) {
    if (quizStarted || !e.target.closest) return;
    if (e.target.closest('[data-quiz-option], [data-quiz-next], [data-quiz-frag]')) quizStarted = true;
  }, true);

  document.addEventListener('quiz:screen', function (e) {
    /* The controller mounts and shows screen 1 on every page that carries a
       quiz, open or not. Without this gate every pageview would report a quiz
       step and completion rate would be measured against the wrong base. */
    if (!quizStarted) return;

    var d = (e && e.detail) || {};
    var screen = d.screen || '';

    /* First real question answered-into = engaged. */
    if (!quizSeen.engaged && screen !== 'intro' && screen !== 'path' && screen !== '') {
      quizSeen.engaged = true;
      send('quiz_engaged', { quiz_step: d.step, quiz_screen: screen },
        { key: 'quiz', value: 'engaged' });
    }

    /* Reaching the results screen is completion. Guarded so a back-and-forward
       through results does not inflate the completion count. */
    if (screen === 'results' && !quizSeen.completed) {
      quizSeen.completed = true;
      send('quiz_complete', { quiz_steps: d.total },
        { key: 'quiz', value: 'completed' });
    }

    /* Step-by-step, for the drop-off curve. */
    send('quiz_step', { quiz_step: d.step, quiz_total: d.total, quiz_screen: screen },
      { key: 'quiz_step', value: String(d.step || '') + ':' + screen });
  });
}());
